import EventEmitter from "../../common/EventEmitter";
import Logger from "../../common/Logger";

class PeerConnection extends EventEmitter {

    constructor({ config, debug, logger } = {}){
        super()
        this.myStream = null
        this.streamExternal = false
        this.mySdp = null
        this.myCandidates = null;
        this.remoteSdp = null;
        this.remoteCandidates = null;
        this.trickle = true
        this.iceDone = false
        this.doDtx = true
        this.mids = {}
        this.debuging = !!debug
        this.config = this.prepareConfiguration(config)
        this.completeTimeout = null
        this.trickleTimeout = null
        this.trackMutedTimeout = null
        this.logger = logger || new Logger('PeerConnection')
        //callbacks
        this.onLogging = (str)=>{
            this.emit('logging', str)
            const hasListeners = this.listeners && this.listeners.logging && this.listeners.logging.length > 0
            if (!hasListeners) this.logger.info(str)
        }
        this.onGetOffer = ({type, sdp, trickle})=>{ this.emit('offer', {type, sdp, trickle}) }
        this.onGetAnswer = ({type, sdp, trickle})=>{ this.emit('answer', {type, sdp, trickle}) }
        this.onIceCandidate = (candidate)=>{ this.emit('candidate', candidate) }
        this.onLocalTrack = ({track, added})=>{ this.emit('local-track', {track, added}) }
        this.onRemoteTrack = ({track, mid, added, metadata})=>{ this.emit('remote-track', {track, mid, added, metadata}) }
        this.onChangeConnectionState = (state)=>{ this.emit('connection-state', state) }
        this.onChangeIceConnectionState = (state)=>{ this.emit('ice-connection-state', state) }
    }

    // Helper function to convert a track object to a set of constraints
    /*
    Track options
    type:
        'audio' - audio track
        'video' - audio track
        'screen' - sharing track
        'data' - data track
    capture:
        true - default settings
        'lowres'      - Small resolution, 4:3, 320x240
        'lowres-16:9' - Small resolution, 16:9, 320x180
        'hires'       - High(HD) resolution is only 16:9, 1280x720
        'hires-16:9'  - High(HD) resolution is only 16:9, 1280x720
        'hdres'       - High(HD) resolution is only 16:9, 1280x720
        'fhdres'      - Full HD resolution is only 16:9, 1920x1080
        '4kres'       - 4K resolution is only 16:9, 3840x2160
        'stdres'      - Normal resolution, 4:3, 640x480
        'stdres-16:9' - Normal resolution, 16:9, 640x360
         null         - Default video setting is stdres 4:3, 640x480
    bitrate: maximum number of bits per second
    framerate: maximum number of frames per second
    dontStop: don't stop track
     */
    static trackConstraints (track) {
        let constraints = {};
        if(!track || !track.capture)
            return constraints;
        if(track.type === 'audio') {
            // Just put the capture part in the constraints
            constraints.audio = track.capture;
        } else if(track.type === 'video') {
            // Check if one of the keywords was passed
            if((track.simulcast || track.svc) && track.capture === true)
                track.capture = 'hires';
            if(track.capture === true || typeof track.capture === 'object') {
                // Use the provided capture object as video constraint
                constraints.video = track.capture;
            } else {
                let width = 0;
                let height = 0;
                if(track.capture === 'lowres') {
                    // Small resolution, 4:3
                    width = 320;
                    height = 240;
                } else if(track.capture === 'lowres-16:9') {
                    // Small resolution, 16:9
                    width = 320;
                    height = 180;
                } else if(track.capture === 'hires' || track.capture === 'hires-16:9' || track.capture === 'hdres') {
                    // High(HD) resolution is only 16:9
                    width = 1280;
                    height = 720;
                } else if(track.capture === 'fhdres') {
                    // Full HD resolution is only 16:9
                    width = 1920;
                    height = 1080;
                } else if(track.capture === '4kres') {
                    // 4K resolution is only 16:9
                    width = 3840;
                    height = 2160;
                } else if(track.capture === 'stdres') {
                    // Normal resolution, 4:3
                    width = 640;
                    height = 480;
                } else if(track.capture === 'stdres-16:9') {
                    // Normal resolution, 16:9
                    width = 640;
                    height = 360;
                } else {
                    this.log('Default video setting is stdres 4:3');
                    width = 640;
                    height = 480;
                }
                constraints.video = {
                    width: { ideal: width },
                    height: { ideal: height }
                };
            }
        } else if(track.type === 'screen') {
            // Use the provided capture object as video constraint
            constraints.video = track.capture;
        }
        return constraints;
    };

    static stopAllTracks (stream) {
        try {
            // Try a MediaStreamTrack.stop() for each track
            let tracks = stream.getTracks();
            for(let mst of tracks) {
                //this.log(mst);
                if(mst && mst.dontStop !== true) {
                    mst.stop();
                }
            }
            // eslint-disable-next-line no-unused-vars
        } catch(e) {
            // Do nothing if this fails
        }
    }

    static logToString = (...args) => {
        let str = ''
        const seen = new WeakSet()
        const safeStringify = (value) => {
            if (value === undefined) return 'undefined'
            if (value === null) return 'null'
            if (typeof value === 'string') return value
            if (typeof value !== 'object') return String(value)
            try {
                return JSON.stringify(value, (key, val) => {
                    if (key === 'sdp' && typeof val === 'string') return `<sdp:${val.length}>`
                    if (typeof val === 'bigint') return val.toString()
                    if (typeof val === 'function') return `[Function${val.name ? ':' + val.name : ''}]`
                    if (val && typeof val === 'object') {
                        if (seen.has(val)) return '[Circular]'
                        seen.add(val)
                    }
                    return val
                })
            } catch (e) {
                return '[Unserializable]'
            }
        }
        args.forEach((arg) => str += (str ? ' ' : '') + safeStringify(arg));
        return str
    }

    log(...args) {
        this.onLogging && this.onLogging(PeerConnection.logToString(...args))
    }

    error(...args){
        if(this.onLogging){
            this.onLogging(PeerConnection.logToString('Error', ...args))
        } else {
            console.log('Error: ', args)
        }
    }

    warn(...args){
        if(!this.debuging) return
        if(this.onLogging){
            this.onLogging(PeerConnection.logToString('Warn', ...args))
        } else {
            console.log('Warn: ',args)
        }
    }

    debug(...args){
        if(!this.debuging) return
        if(this.onLogging){
            this.onLogging(PeerConnection.logToString('Debug', ...args))
        } else {
            console.log('Debug: ',args)
        }
    }

    prepareConfiguration ({iceServers, iceTransportPolicy, bundlePolicy, trickle }) {
        this.trickle = trickle
        return {
            iceServers: iceServers, //[{urls: "stun:stun.l.google.com:19302"}]
            iceTransportPolicy: iceTransportPolicy, //"all", "public", "relay"
            bundlePolicy: bundlePolicy, // "balanced", "max-compat", "max-bundle"
            sdpSemantics: 'unified-plan'
        }
    }

    init() {
        this.debug('Creating PeerConnection', {config: this.config});
        this.pc = new RTCPeerConnection(this.config)
        if(!this.pc) return
        this.debug('Preparing PeerConnection (trickle=' + this.trickle + ')');
        this.pc.onconnectionstatechange = () => {
            if(this.pc)
                this.onChangeConnectionState(this.pc.connectionState);
        };
        this.pc.oniceconnectionstatechange = () => {
            if(this.pc)
                this.onChangeIceConnectionState(this.pc.iceConnectionState);
        };
        this.pc.onicecandidate = (event) => {
            this.debug('candidates: ', event.candidate);
            if(!event.candidate || (event.candidate.candidate && event.candidate.candidate.indexOf('endOfCandidates') > 0)) {
                this.debug('End of candidates.');
                if(this.trickle === true || this.iceDone === true) {
                    this.iceDone = true;
                    // Notify end of candidates
                    //this.onIceCandidate({ completed : true });
                } else {
                    // No trickle, time to send the complete SDP (including all candidates)
                    this.onCompleteSdp()
                }
            } else {
                // JSON.stringify doesn't work on some WebRTC objects anymore
                // See https://code.google.com/p/chromium/issues/detail?id=467366
                let candidate = {
                    candidate: event.candidate.candidate,
                    sdpMid: event.candidate.sdpMid,
                    sdpMLineIndex: event.candidate.sdpMLineIndex
                };
                if(this.trickle === true || this.iceDone) {
                    if(this.trickleTimeout) {
                        // wait remote initialization
                        if(!this.myCandidates) this.myCandidates = []
                        this.myCandidates.push(candidate)
                    } else {
                        // Send candidate
                        this.onIceCandidate(candidate);
                    }
                }
            }
        };
        this.pc.ontrack = (event) => {
            this.debug('Handling Remote Track', event);
            if(!event.streams)
                return;
            if(!event.track)
                return;
            // Notify about the new track event
            let mid = event.transceiver ? event.transceiver.mid : event.track.id;
            try {
                if(event.transceiver && event.transceiver.mid && event.track.id) {
                    // Keep track of the mapping between track ID and mid, since
                    // when a track is removed the transceiver may be gone already
                    if(!this.mids)
                        this.mids = {};
                    this.mids[event.track.id] = event.transceiver.mid;
                }
                this.onRemoteTrack({track:event.track, mid, added:true, metadata:{ reason: 'created' }});
            } catch(e) {
                this.error("Error calling onRemoteTrack", e.message);
            }
            if(event.track.onended)
                return;
            this.debug('Adding onended callback to track:', event.track);
            event.track.onended = (ev) => {
                this.debug('Remote track removed:', ev);
                if(this.trackMutedTimeout) {
                    clearTimeout(this.trackMutedTimeout);
                }
                // Notify the application
                let transceivers = this.pc ? this.pc.getTransceivers() : null;
                let transceiver = transceivers ? transceivers.find(
                    t => t.receiver.track === ev.target) : null;
                let mid = transceiver ? transceiver.mid : ev.target.id;
                if(mid === ev.target.id && this.mids && this.mids[event.track.id])
                    mid = this.mids[event.track.id];
                try {
                    this.onRemoteTrack({track:ev.target, mid, added:false, metadata:{ reason: 'ended' }});
                } catch(e) {
                    this.error("Error calling onRemoteTrack on removal", e.message);
                }
                delete this.mids[event.track.id];
            };
            event.track.onmute = (ev) => {
                this.debug('Remote track muted:', ev);
                if(!this.trackMutedTimeout) {
                    this.trackMutedTimeout = setTimeout(() => {
                        this.debug('Removing remote track');
                        // Notify the application the track is gone
                        let transceivers = this.pc ? this.pc.getTransceivers() : null;
                        let transceiver = transceivers ? transceivers.find(
                            t => t.receiver.track === ev.target) : null;
                        let mid = transceiver ? transceiver.mid : ev.target.id;
                        if(mid === ev.target.id && this.mids && this.mids[event.track.id])
                            mid = this.mids[event.track.id];
                        try {
                            this.onRemoteTrack({track:ev.target, mid, added:false, metadata:{ reason: 'mute' }});
                        } catch(e) {
                            this.error("Error calling onRemoteTrack on mute", e.message);
                        }
                        this.trackMutedTimeout = null;
                        // Chrome seems to raise mute events only at multiples of 834ms;
                        // we set the timeout to three times this value (rounded to 840ms)
                    }, 3 * 840);
                }
            };
            event.track.onunmute = (ev) => {
                this.debug('Remote track flowing again:', ev);
                if(this.trackMutedTimeout != null) {
                    clearTimeout(this.trackMutedTimeout);
                    this.trackMutedTimeout = null;
                } else {
                    try {
                        // Notify the application the track is back
                        let transceivers = this.pc ? this.pc.getTransceivers() : null;
                        let transceiver = transceivers ? transceivers.find(
                            t => t.receiver.track === ev.target) : null;
                        let mid = transceiver ? transceiver.mid : ev.target.id;
                        this.onRemoteTrack({track:ev.target, mid, added:true, metadata:{ reason: 'unmute' }});
                    } catch(e) {
                        this.error("Error calling onRemoteTrack on unmute", e.message);
                    }
                }
            };
        };
    }

    setMediaDevices(mediaDevices) {
        if(!mediaDevices) this.error("Can't add media devices")
        this.mediaDevices = mediaDevices
    }

    addCandidate(candidate) {
        if(this.pc && this.remoteSdp) {
            // Add candidate right now
            this.debug("Adding remote candidate:" + JSON.stringify(candidate));
            if(!candidate || candidate.completed === true) {
                // end-of-candidates
                this.pc.addIceCandidate(null);
            } else {
                // New candidate
                this.pc.addIceCandidate(candidate);
            }
        } else {
            // We didn't do setRemoteDescription (trickle got here before the offer?)
            this.debug("We didn't do setRemoteDescription (trickle got here before the offer?), caching candidate");
            if(!this.remoteCandidates)
                this.remoteCandidates = [];
            this.remoteCandidates.push(candidate);
            this.debug(this.remoteCandidates);
        }
    }

    async createOffer ({tracks, media, simulcast, svc}) {
        if( ! this.prepareWebrtc({offer: true, tracks, media, simulcast, svc}) ) return

        try {
            // Capture devices and setup tracks, if needed
            await this.captureDevices({tracks});
            this.debug("Creating offer (iceDone=" + this.iceDone + ")");
            let offer = await this.pc.createOffer();
            this.debug(offer);
            // JSON.stringify doesn't work on some WebRTC objects anymore
            // See https://code.google.com/p/chromium/issues/detail?id=467366

            if(this.doDtx) {
                offer.sdp = offer.sdp
                    .replace("useinbandfec=1", "useinbandfec=1;usedtx=1")
            }

            this.debug("Setting local description");
            this.mySdp = {
                type: 'offer',
                sdp: offer.sdp
            };
            await this.pc.setLocalDescription(offer);

            if(!this.iceDone && !this.trickle) {
                // FIXME Don't do anything until we have all candidates
                this.debug("Waiting for all candidates...");
                this.setOnCompleteSdpTimeout();
                return null;
            }
            this.setOnTrickleTimeout()
            this.onGetOffer(this.mySdp)
            return this.mySdp;
        } catch(err) {
            this.error(err.message);
        }
    }

    async createAnswer ({jsep, tracks, media, simulcast, svc}) {
        if( ! this.prepareWebrtc({offer: false, jsep, tracks, media, simulcast, svc}) ) return

        try {
            await this.pc.setRemoteDescription(jsep);
            this.debug("Remote description accepted!");
            this.remoteSdp = jsep.sdp;
            // Any trickle candidate we cached?
            if(this.remoteCandidates && this.remoteCandidates.length > 0) {
                for(let i=0; i<this.remoteCandidates.length; i++) {
                    let candidate = this.remoteCandidates[i];
                    this.debug("Adding remote candidate:" + candidate);
                    if(!candidate || candidate.completed === true) {
                        // end-of-candidates
                        this.pc.addIceCandidate(null);
                    } else {
                        // New candidate
                        this.pc.addIceCandidate(candidate);
                    }
                }
                this.remoteCandidates = [];
            }
            // Capture devices and setup tracks, if needed
            await this.captureDevices({tracks, jsep});
            // Create the answer now
            this.debug("Creating answer (iceDone=" + this.iceDone + ")");
            let answer = await this.pc.createAnswer();
            this.debug(answer);
            // JSON.stringify doesn't work on some WebRTC objects anymore
            // See https://code.google.com/p/chromium/issues/detail?id=467366

            const stereo = (jsep.sdp.indexOf("stereo=1") !== -1);

            if(stereo && answer.sdp.indexOf("stereo=1") == -1) {
                // Make sure that our offer contains stereo too
                answer.sdp = answer.sdp.replace("useinbandfec=1", "useinbandfec=1;stereo=1");
            }
            this.debug("Setting local description");
            this.mySdp = {
                type: 'answer',
                sdp: answer.sdp
            };
            await this.pc.setLocalDescription(answer);
            if(!this.iceDone && !this.trickle) {
                // FIXME Don't do anything until we have all candidates
                this.debug("Waiting for all candidates...");
                this.setOnCompleteSdpTimeout();
                return null;
            }
            this.setOnTrickleTimeout()
            this.onGetAnswer(this.mySdp)
            return answer;
        } catch(err) {
            this.error(err.message);
        }
    }

    async setAnswer (jsep) {
        if(!this.pc) {
            this.warn("Invalid PeerConnection");
            return;
        }
        if(!jsep) {
            this.warn("Invalid jsep");
            return;
        }
        await this.pc.setRemoteDescription(jsep);
        this.debug("Remote description accepted!")
        this.remoteSdp = jsep.sdp;
        // Any trickle candidate we cached?
        if(this.remoteCandidates && this.remoteCandidates.length > 0) {
            for(let i=0; i<this.remoteCandidates.length; i++) {
                let candidate = this.remoteCandidates[i];
                this.debug("Adding remote candidate:", candidate);
                if(!candidate || candidate.completed === true) {
                    // end-of-candidates
                    this.pc.addIceCandidate(null);
                } else {
                    // New candidate
                    this.pc.addIceCandidate(candidate);
                }
            }
            this.remoteCandidates = [];
        }
    }

    prepareWebrtc({offer, jsep, tracks}) {
        if(!this.pc) {
            this.warn("Invalid PeerConnection");
            return false;
        }
        if(offer && jsep) {
            this.error("Provided a JSEP to a createOffer");
            return false;
        } else if(!offer && (!jsep || !jsep.type || !jsep.sdp)) {
            this.error("A valid JSEP is required for createAnswer");
            return false;
        }
        // Check that callbacks.array is a valid array
        if(tracks && !Array.isArray(tracks)) {
            this.error("Tracks must be an array");
            return false;
        }
        return true
    }

    setOnCompleteSdpTimeout () {
        if (this.completeTimeout) {
            clearTimeout(this.completeTimeout)
        }
        this.completeTimeout = setTimeout(()=>{this.onCompleteSdp()}, 2000)
    }

    setOnTrickleTimeout () {
        if (this.trickleTimeout) {
            clearTimeout(this.trickleTimeout)
        }
        this.trickleTimeout = setTimeout(()=>{this.onTrickleReady()}, 100)
    }

    onTrickleReady(){
        if (this.trickleTimeout) {
            clearTimeout(this.trickleTimeout)
            this.trickleTimeout = null
        }
        if(this.myCandidates && this.myCandidates.length ) {
            for(let i=0; i<this.myCandidates.length; i++) {
                let candidate = this.myCandidates[i];
                this.debug("Send trickle candidate:" + JSON.stringify(candidate));
                this.onIceCandidate(candidate);
            }
        }
        this.myCandidates = null
    }

    onCompleteSdp(){
        if (this.completeTimeout) {
            clearTimeout(this.completeTimeout)
            this.completeTimeout = null
        }
        this.iceDone = true;
        if(!this.mySdp) {
            this.warn("Local SDP instance is invalid, not sending anything...");
            return;
        }
        this.mySdp = {
            type: this.pc.localDescription.type,
            sdp: this.pc.localDescription.sdp,
            trickle: this.trickle
        };
        if(this.mySdp.type === 'offer'){
            this.debug("Create offer SDP with candidates...");
            this.onGetOffer(this.mySdp)
        } else {
            this.debug("Create answer SDP with candidates...");
            this.onGetAnswer(this.mySdp)
        }
    }

    async captureDevices({tracks, jsep}) {
        if(!this.mediaDevices) {
            this.warn('Invalid mediaDevices');
            throw 'Invalid mediaDevices';
        }
        if(!this.pc) {
            this.warn('Invalid PeerConnection');
            throw 'Invalid PeerConnection';
        }
        if(!tracks || !Array.isArray(tracks) || tracks.length === 0) {
            // Nothing to do
            return;
        }
        let answer = (jsep ? true : false);
        for(let track of tracks) {
            if(!track.type) {
                this.warn('Missing track type:', track);
                continue;
            }
            if(track.type === 'data') {
                this.warn('Data channel not supported');
                continue;
            }
            if((typeof track.add === 'undefined' || track.add === null) &&
                (typeof track.remove === 'undefined' || track.remove === null) &&
                (typeof track.replace === 'undefined' || track.replace === null)) {
                // Let's default to 'add'
                track.add = true;
            }
            if((track.add && track.remove) || (track.add && track.remove && track.replace)) {
                this.warn('Conflicting actions for track, ignoring:', track);
                continue;
            }
            if(track.add && track.replace) {
                this.warn('Both add and replace provided, falling back to replace:', track);
                delete track.add;
            } else if(track.remove && track.replace) {
                this.warn('Both remove and replace provided, falling back to remove:', track);
                delete track.replace;
            }
            let kind = track.type;
            if(track.type === 'screen')
                kind = 'video';	// FIXME
            let transceiver = null, sender = null;
            if(track.mid) {
                // Search by mid
                transceiver = this.pc.getTransceivers()
                    .find(t => (t.mid === track.mid && t.receiver.track.kind === kind));
            } else if(!track.add) {
                // Find the first track of this type
                transceiver = this.pc.getTransceivers()
                    .find(t => (t.receiver.track.kind === kind));
            }
            if(track.replace || track.remove) {
                if(!transceiver) {
                    this.warn("Couldn't find a transceiver for track:", track);
                    continue;
                }
                if(!transceiver.sender) {
                    this.warn('No sender in the transceiver for track:', track);
                    continue;
                }
                sender = transceiver.sender;
            }
            if(answer && !transceiver) {
                transceiver = this.pc.getTransceivers()
                    .find(t => (t.receiver.track.kind === kind));
                if(!transceiver) {
                    this.warn("Couldn't find a transceiver for track:", track);
                    continue;
                }
            }
            // Capture the new track, if we need to
            let nt = null, trackId = null;
            if(track.remove || track.replace) {
                this.log('Removing track from PeerConnection', track);
                trackId = sender.track ? sender.track.id : null;
                await sender.replaceTrack(null);
                // Get rid of the old track
                if(trackId && this.myStream) {
                    let rt = null;
                    if(kind === 'audio' && this.myStream.getAudioTracks() && this.myStream.getAudioTracks().length) {
                        for(let t of this.myStream.getAudioTracks()) {
                            if(t.id === trackId) {
                                rt = t;
                                this.log('Removing audio track:', rt);
                            }
                        }
                    } else if(kind === 'video' && this.myStream.getVideoTracks() && this.myStream.getVideoTracks().length) {
                        for(let t of this.myStream.getVideoTracks()) {
                            if(t.id === trackId) {
                                rt = t;
                                this.log('Removing video track:', rt);
                            }
                        }
                    }
                    if(rt) {
                        // Remove the track and notify the application
                        try {
                            this.myStream.removeTrack(rt);
                            this.onLocalTrack({track:rt, added:false});
                        } catch(e) {
                            this.error("Error calling onLocalTrack on removal for renegotiation", e.message);
                        }
                        // Close the old track (unless we've been asked not to)
                        if(rt.dontStop !== true) {
                            try {
                                rt.stop();
                                // eslint-disable-next-line no-unused-vars
                            } catch(e) {}
                        }
                    }
                }
            }
            if(track.capture) {
                let constraints = PeerConnection.trackConstraints(track), stream = null;
                if(track.type === 'audio' || track.type === 'video') {
                    stream = await this.mediaDevices.getUserMedia(constraints);
                } else {
                    // Use getDisplayMedia
                    stream = await this.mediaDevices.getDisplayMedia(constraints);
                }
                nt = (track.type === 'audio' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0]);

                if(track.replace) {
                    // Replace the track
                    await sender.replaceTrack(nt);
                    // Update the transceiver direction
                    let newDirection = 'sendrecv';
                    if(track.recv === false || transceiver.direction === 'inactive' || transceiver.direction === 'sendonly')
                        newDirection = 'sendonly';
                    if(transceiver.setDirection)
                        transceiver.setDirection(newDirection);
                    else
                        transceiver.direction = newDirection;
                } else {
                    // FIXME Add as a new track
                    if(!this.myStream)
                        this.myStream = new MediaStream();
                    if(kind === 'audio') {
                        sender = this.pc.addTrack(nt, this.myStream);
                        transceiver = this.pc.getTransceivers()
                            .find(t => (t.sender === sender));
                    } else {
                        transceiver = this.pc.addTransceiver(nt, {
                            direction: 'sendrecv',
                            streams: [this.myStream],
                            sendEncodings: [
                                { scalabilityMode: track.svc }
                            ]
                        });
                    }
                    if(!sender)
                        sender = transceiver ? transceiver.sender : null;
                    if(track.bitrate) {
                        // Override maximum bitrate
                        if(sender) {
                            let params = sender.getParameters();
                            if(!params || !params.encodings || params.encodings.length === 0) {
                                this.warn('No encodings in the sender parameters, ignoring bitrate for track:', track);
                            } else {
                                params.encodings[0].maxBitrate = track.bitrate;
                                await sender.setParameters(params);
                            }
                        }
                    }
                    if(kind === 'video' && track.framerate) {
                        // Override maximum framerate
                        if(sender) {
                            let params = sender.getParameters();
                            if(!params || !params.encodings || params.encodings.length === 0) {
                                this.warn('No encodings in the sender parameters, ignoring framerate for track:', track);
                            } else {
                                params.encodings[0].maxFramerate = track.framerate;
                                await sender.setParameters(params);
                            }
                        }
                    }
                }
                if(nt && track.dontStop === true)
                    nt.dontStop = true;
            }
            if(nt) {
                // FIXME Add the new track locally
                this.myStream.addTrack(nt);
                // Notify the application about the new local track, if any
                nt.onended = (ev) => {
                    this.log('Local track removed:', ev);
                    try {
                        this.onLocalTrack({track:ev.target, added:false});
                    } catch(e) {
                        this.error("Error calling onLocalTrack following end", e.message);
                    }
                }
                try {
                    this.onLocalTrack({track:nt, added:true});
                } catch(e) {
                    this.error("Error calling onLocalTrack for track add", e.message);
                }
            }
            // Update the direction of the transceiver
            if(transceiver) {
                let curdir = transceiver.direction, newdir = null;
                let send = (nt && transceiver.sender.track),
                    recv = (track.recv !== false && transceiver.receiver.track);
                if(send && recv)
                    newdir = 'sendrecv';
                else if(send && !recv)
                    newdir = 'sendonly';
                else if(!send && recv)
                    newdir = 'recvonly';
                else if(!send && !recv)
                    newdir = 'inactive';
                if(newdir && newdir !== curdir) {
                    this.warn('Changing direction of transceiver to ' + newdir + ' (was ' + curdir + ')', track);
                    if(transceiver.setDirection)
                        transceiver.setDirection(newdir);
                    else
                        transceiver.direction = newdir;
                }
            }
        }
    }

    mute(mid, video, mute) {
        if(!this.pc) {
            this.warn("Invalid PeerConnection");
            return false;
        }
        if(!this.myStream) {
            this.warn("Invalid local MediaStream");
            return false;
        }
        if(video) {
            // Mute/unmute video track
            if(!this.myStream.getVideoTracks() || this.myStream.getVideoTracks().length === 0) {
                this.warn("No video track");
                return false;
            }
            if(mid) {
                let transceiver = this.pc.getTransceivers()
                    .find(t => (t.mid === mid && t.receiver.track.kind === "video"));
                if(!transceiver) {
                    this.warn("No video transceiver with mid " + mid);
                    return false;
                }
                if(!transceiver.sender || !transceiver.sender.track) {
                    this.warn("No video sender with mid " + mid);
                    return false;
                }
                transceiver.sender.track.enabled = mute ? false : true;
            } else {
                for(const videostream of this.myStream.getVideoTracks()) {
                    videostream.enabled = !mute
                }
            }
        } else {
            // Mute/unmute audio track
            if(!this.myStream.getAudioTracks() || this.myStream.getAudioTracks().length === 0) {
                this.warn("No audio track");
                return false;
            }
            if(mid) {
                let transceiver = this.pc.getTransceivers()
                    .find(t => (t.mid === mid && t.receiver.track.kind === "audio"));
                if(!transceiver) {
                    this.warn("No audio transceiver with mid " + mid);
                    return false;
                }
                if(!transceiver.sender || !transceiver.sender.track) {
                    this.warn("No audio sender with mid " + mid);
                    return false;
                }
                transceiver.sender.track.enabled = mute ? false : true;
            } else {
                for(const audiostream of this.myStream.getAudioTracks()) {
                    audiostream.enabled = !mute
                }
            }
        }
        return true;
    }

    close() {
        this.debug("Cleaning WebRTC");
        // Cleanup stack
        if(this.volume) {
            if(this.volume["local"] && this.volume["local"].timer)
                clearInterval(this.volume["local"].timer);
            if(this.volume["remote"] && this.volume["remote"].timer)
                clearInterval(this.volume["remote"].timer);
        }
        if(!this.streamExternal && this.myStream) {
            this.debug("Stopping local stream tracks");
            PeerConnection.stopAllTracks(this.myStream);
        }
        this.streamExternal = false;
        this.myStream = null;
        // Close PeerConnection
        try {
            this.pc.close();
            // eslint-disable-next-line no-unused-vars
        } catch(e) {
            // Do nothing
        }
        this.pc = null;
        this.mySdp = null;
        this.myCandidates = null;
        this.remoteSdp = null;
        this.remoteCandidates = null;
        this.iceDone = false;
        if (this.completeTimeout) {
            clearTimeout(this.completeTimeout)
        }
        if (this.trickleTimeout) {
            clearTimeout(this.trickleTimeout)
        }
        if (this.trackMutedTimeout) {
            clearTimeout(this.trackMutedTimeout)
        }
        this.clearListeners()

    }

    getConnectionState = () =>{
        if(!this.pc) return null
        return this.pc.connectionState;
    }
}

export default PeerConnection
